import currencyFormatter from '~/utils/currencyFormatter';

import type { CommercialApprovalData } from './types';

function escapeHtml(value: string | null | undefined): string {
  if (value == null) {
    return '';
  }
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function money(value: number | null | undefined, currency = 'AUD'): string {
  return value == null ? '—' : currencyFormatter(currency, value, 0);
}

function pct(value: number | null | undefined): string {
  return value == null ? '—' : `${value.toFixed(1)}%`;
}

function placeholderNote(show: boolean, message: string): string {
  return show ? `<div class="placeholder-note">${message}</div>` : '';
}

function prvTile(label: string, valueHtml: string): string {
  return `<div class="deal-info-field"><div class="deal-info-label">${label}</div><div class="deal-info-value">${valueHtml}</div></div>`;
}

function prvColor(value: number | null, goodAt: number, warnAt: number): string {
  if (value == null) {
    return 'inherit';
  }
  if (value >= goodAt) {
    return '#2E7D32';
  }
  if (value >= warnAt) {
    return '#ED6C02';
  }
  return '#D32F2F';
}

function leadSellPriceSection(data: CommercialApprovalData): string {
  const l = data.leadSellPrice;
  const otaColor =
    l.avgSellVsMinOtaPct == null
      ? 'inherit'
      : l.avgSellVsMinOtaPct > 0
        ? '#D32F2F'
        : l.avgSellVsMinOtaPct < 0
          ? '#2E7D32'
          : '#5F6168';
  const otaSign = l.avgSellVsMinOtaPct != null && l.avgSellVsMinOtaPct > 0 ? '+' : '';
  const below30Color = l.below30PrvCount != null && l.below30PrvCount > 0 ? '#ED6C02' : '#2E7D32';
  const negativeColor = l.negativePrvCount != null && l.negativePrvCount > 0 ? '#D32F2F' : '#5F6168';

  return `<div class="card"><h2>Lead Sell Price</h2>${placeholderNote(data.isPlaceholderData.leadSellPrice, 'Run a rate scrape on the Scrape tab to populate PRV data')}
    <div class="lead-sell-value">${l.sellAud != null ? `AUD ${Math.round(l.sellAud).toLocaleString('en-AU')}` : '—'}</div>
    <p class="note">Net rate: ${money(l.netRate)} · ${pct(l.marginPct)} margin</p>
    <div class="deal-info-grid" style="grid-template-columns:repeat(auto-fit,minmax(132px,1fr));margin-top:1rem;">
      ${prvTile('Avg PRV', `<span style="color:${prvColor(l.avgPrv, 30, 15)}">${l.avgPrv != null ? `${l.avgPrv.toFixed(1)}%` : '—'}</span>`)}
      ${prvTile('Range', l.minPrv != null && l.maxPrv != null ? `${l.minPrv.toFixed(1)}% – ${l.maxPrv.toFixed(1)}%` : '—')}
      ${prvTile('Avg sell vs min OTA', `<span style="color:${otaColor}">${l.avgSellVsMinOtaPct != null ? `${otaSign}${l.avgSellVsMinOtaPct.toFixed(1)}%` : '—'}</span>`)}
      ${prvTile('Below 30% PRV', `<span style="color:${below30Color}">${l.below30PrvCount ?? '—'}/${l.below30PrvTotal ?? '—'}</span>`)}
      ${prvTile('Negative PRV', `<span style="color:${negativeColor}">${l.negativePrvCount ?? '—'}/${l.negativePrvTotal ?? '—'}</span>`)}
    </div></div>`;
}

const PRV_CHART_WIDTH = 900;
const PRV_CHART_HEIGHT = 300;
const PRV_CHART_PLOT = { top: 20, bottom: 230, left: 75, right: 825 };

function prvChartSection(data: CommercialApprovalData): string {
  const points = data.prvChart;
  if (points.length === 0) {
    return '';
  }

  const rates = points.flatMap((p) => [p.avgOta, p.leSell]).filter((v): v is number => v != null);
  const maxRate = Math.max(...rates, 1) * 1.1;
  const plotWidth = PRV_CHART_PLOT.right - PRV_CHART_PLOT.left;
  const plotHeight = PRV_CHART_PLOT.bottom - PRV_CHART_PLOT.top;

  const scaleX = (index: number) =>
    PRV_CHART_PLOT.left + (points.length === 1 ? 0 : (index / (points.length - 1)) * plotWidth);
  const scaleRate = (value: number) => PRV_CHART_PLOT.bottom - (value / maxRate) * plotHeight;
  const scalePrv = (value: number) => PRV_CHART_PLOT.bottom - (Math.max(0, Math.min(100, value)) / 100) * plotHeight;

  const linePoints = (values: Array<number | null>, scale: (v: number) => number) =>
    values
      .map((v, i) => (v == null ? null : `${scaleX(i).toFixed(1)},${scale(v).toFixed(1)}`))
      .filter((p): p is string => p != null)
      .join(' ');

  const gridLines = [0, 0.25, 0.5, 0.75, 1]
    .map((fraction) => {
      const y = PRV_CHART_PLOT.top + fraction * plotHeight;
      return `<line x1="${PRV_CHART_PLOT.left}" y1="${y}" x2="${PRV_CHART_PLOT.right}" y2="${y}" stroke="#E8EAEE" stroke-width="1"/>
        <text x="${PRV_CHART_PLOT.left - 8}" y="${y + 4}" font-size="10" fill="#5F6168" text-anchor="end">$${Math.round(maxRate * (1 - fraction)).toLocaleString('en-AU')}</text>
        <text x="${PRV_CHART_PLOT.right + 8}" y="${y + 4}" font-size="10" fill="#5F6168" text-anchor="start">${Math.round(100 * (1 - fraction))}%</text>`;
    })
    .join('');

  const xLabels = points
    .map(
      (p, i) =>
        `<text x="${scaleX(i).toFixed(1)}" y="${PRV_CHART_PLOT.bottom + 16}" font-size="10" fill="#5F6168" text-anchor="middle">${escapeHtml(p.month)}</text>`,
    )
    .join('');

  const midY = (PRV_CHART_PLOT.top + PRV_CHART_PLOT.bottom) / 2;
  const axisTitles = `<text x="18" y="${midY}" font-size="10" font-weight="600" fill="#5F6168" text-anchor="middle" transform="rotate(-90 18 ${midY})">Rate (AUD)</text>
      <text x="${PRV_CHART_WIDTH - 18}" y="${midY}" font-size="10" font-weight="600" fill="#5F6168" text-anchor="middle" transform="rotate(90 ${PRV_CHART_WIDTH - 18} ${midY})">PRV %</text>`;

  // One list drives both the drawn lines and the legend, so they can't drift out of sync -
  // the 30% Target is a real (flat) series here, not a separately hand-drawn reference line.
  const series: Array<{
    label: string;
    color: string;
    dashed: boolean;
    values: Array<number | null>;
    scale: (v: number) => number;
  }> = [
    { label: 'Avg OTA', color: '#0288D1', dashed: false, values: points.map((p) => p.avgOta), scale: scaleRate },
    { label: 'LE Sell', color: '#00C9A0', dashed: false, values: points.map((p) => p.leSell), scale: scaleRate },
    {
      label: 'Median PRV %',
      color: '#ED6C02',
      dashed: false,
      values: points.map((p) => p.medianPrvPct),
      scale: scalePrv,
    },
    { label: '30% Target', color: '#888C94', dashed: true, values: points.map(() => 30), scale: scalePrv },
  ];

  const lines = series
    .map(
      ({ color, dashed, values, scale }) =>
        `<polyline points="${linePoints(values, scale)}" fill="none" stroke="${color}" stroke-width="2"${dashed ? ' stroke-dasharray="4,3"' : ''}/>`,
    )
    .join('');

  const legend = series
    .map(({ color, label, dashed }) => {
      const marker = dashed
        ? `<span style="width:12px;height:2px;background-image:repeating-linear-gradient(to right, ${color} 0 4px, transparent 4px 7px);display:inline-block;"></span>`
        : `<span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;"></span>`;
      return `<span style="display:inline-flex;align-items:center;gap:5px;margin-right:16px;">${marker}${label}</span>`;
    })
    .join('');

  return `<div class="card" data-pdf-section><h2>PRV Chart</h2>${placeholderNote(data.isPlaceholderData.prvChart, 'Run a rate scrape on the Scrape tab to populate PRV data')}
    <svg viewBox="0 0 ${PRV_CHART_WIDTH} ${PRV_CHART_HEIGHT}" style="width:100%;height:auto;">
      ${gridLines}
      ${lines}
      ${xLabels}
      ${axisTitles}
    </svg>
    <div style="display:flex;justify-content:center;align-items:center;flex-wrap:wrap;font-size:11px;color:var(--grey-600);margin-top:4px;">${legend}</div>
    </div>`;
}

function prvByMonthSection(data: CommercialApprovalData): string {
  if (data.prvByMonth.length === 0) {
    return '';
  }
  const leadLos = Math.min(...data.lengthsOfStay);
  const rows = data.prvByMonth
    .map((row) => {
      const otaColor =
        row.avgOta != null && row.leSell != null && row.avgOta < row.leSell * 1.3
          ? 'background:#FDECEA;'
          : row.avgOta != null && row.leSell != null && row.avgOta > row.leSell * 3
            ? 'background:#E6F4EA;'
            : '';
      return `<tr><td>${escapeHtml(row.month)}</td><td>${pct(row.medianPrvPct)}</td><td style="${otaColor}">${money(row.avgOta)}</td><td>${money(
        row.lowOta,
      )}</td><td>${money(row.indicativePrice)}</td><td>${money(row.leSell)}</td></tr>`;
    })
    .join('');
  const discountBox =
    data.prvByMonthDiscountPct != null
      ? `<div style="display:flex;justify-content:flex-end;align-items:center;gap:8px;margin:0 0 10px;">
      <span style="font-size:12px;font-weight:600;">Discount:</span>
      <span class="discount-box">${data.prvByMonthDiscountPct}</span>
      <span style="font-size:12px;font-weight:600;">%</span>
    </div>`
      : '';

  return `<div class="card" data-pdf-section><h2>PRV by Month — ${leadLos}-night package</h2>${placeholderNote(
    data.isPlaceholderData.prvByMonth,
    'Run a rate scrape on the Scrape tab to populate PRV data',
  )}
    ${discountBox}
    <table><thead><tr><th>Month</th><th>Median PRV %</th><th>Avg OTA</th><th>Low OTA</th><th>Ind. Price</th><th>LE Sell</th></tr></thead>
    <tbody>${rows}</tbody></table></div>`;
}

function inclusionsSection(data: CommercialApprovalData): string {
  if (data.inclusions.length === 0) {
    return '';
  }
  const rows = data.inclusions
    .map((inclusion) => {
      const basis = [inclusion.type, inclusion.applies].filter(Boolean).join(' · ');
      return `<tr><td>${escapeHtml(inclusion.name)}</td><td>${escapeHtml(basis)}</td><td>${money(inclusion.prv ?? null)}</td></tr>`;
    })
    .join('');
  return `<div class="card"><h2>Inclusions <span style="font-weight:400;font-size:11px;color:var(--grey-600);">Total: ${money(
    data.inclusionsTotalAud,
  )}</span></h2>
    <table><thead><tr><th>Description</th><th>Basis</th><th>Value (AUD)</th></tr></thead>
    <tbody>${rows}</tbody></table></div>`;
}

function blackoutsSection(data: CommercialApprovalData): string {
  if (data.blackouts.length === 0) {
    return '';
  }
  const rows = data.blackouts
    .map(
      (blackout) =>
        `<tr><td>${escapeHtml(blackout.name)}</td><td>${blackout.startDate}</td><td>${blackout.endDate}</td></tr>`,
    )
    .join('');
  return `<div class="card" data-pdf-section><h2>Blackout Periods</h2>
    <table><thead><tr><th>Name</th><th>Start</th><th>End</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function overridesSection(data: CommercialApprovalData): string {
  if (data.overrides.tiers.length === 0) {
    return '';
  }
  const basisLabel = data.overrides.basis === 'room_nights' ? 'Room Nights' : 'Vendor Revenue';
  // Only the first tier ever carries an explicit `from` - later tiers derive it from the
  // previous tier's `to + 1`, same as the mockup's own override-table rendering.
  const rows = data.overrides.tiers
    .map((tier, index) => {
      const previousTo = index > 0 ? data.overrides.tiers[index - 1].to : undefined;
      const from = index === 0 ? (tier.from ?? 0) : (previousTo ?? 0) + 1;
      const to = tier.to != null ? tier.to.toLocaleString('en-AU') : '—';
      return `<tr><td>${from.toLocaleString('en-AU')}</td><td>${to}</td><td style="text-align:right;">${tier.comms ?? 0}%</td></tr>`;
    })
    .join('');
  return `<div class="card" data-pdf-section><h2>Overrides — ${escapeHtml(basisLabel)}</h2>
    <table><thead><tr><th>From</th><th>To</th><th style="text-align:right;">Comms %</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function dealInformationSection(data: CommercialApprovalData): string {
  const d = data.dealInformation;
  return `<div class="card" data-pdf-section><h2>Deal Information</h2>${placeholderNote(
    data.isPlaceholderData.dealInformation,
    'Loading Opportunity details…',
  )}
    <div class="deal-info-grid">
      <div class="deal-info-field"><div class="deal-info-label">Sales forecast AUD</div><div class="deal-info-value">${money(
        d.salesForecastAud,
      )}</div></div>
      <div class="deal-info-field"><div class="deal-info-label">Margin forecast AUD</div><div class="deal-info-value">${money(
        d.marginForecastAud,
      )}</div></div>
      <div class="deal-info-field"><div class="deal-info-label">Tier</div><div class="deal-info-value">${
        d.tier != null ? escapeHtml(d.tier) : '—'
      }</div></div>
      <div class="deal-info-field"><div class="deal-info-label">Weighting</div><div class="deal-info-value">${
        d.weighting ?? '—'
      }</div></div>
      <div class="deal-info-field"><div class="deal-info-label">Offline run period</div><div class="deal-info-value">${
        d.offlineRunPeriod != null ? escapeHtml(d.offlineRunPeriod) : '—'
      }</div></div>
      <div class="deal-info-field"><div class="deal-info-label">Payment method</div><div class="deal-info-value">${
        d.paymentMethod != null ? escapeHtml(d.paymentMethod) : '—'
      }</div></div>
    </div></div>`;
}

function marginColor(marginPct: number): string {
  if (marginPct < 15) {
    return '#D32F2F';
  }
  if (marginPct < 20) {
    return '#ED6C02';
  }
  return '#2E7D32';
}

function forecastSummarySection(data: CommercialApprovalData): string {
  const f = data.forecastSummary;
  const overrideCommsValue =
    f.overrideCommsAud != null
      ? `${f.overrideCommsPct != null ? `<span style="font-size:10px;color:#888C94;margin-right:4px;">${f.overrideCommsPct.toFixed(1)}% of vendor rev</span>` : ''}<span style="color:#2E7D32;font-weight:500;">${money(f.overrideCommsAud)}</span>`
      : '—';
  const marginPctValue =
    f.marginPct != null
      ? `<span style="color:${marginColor(f.marginPct)};font-weight:500;">${pct(f.marginPct)}</span>`
      : '—';

  const rows: Array<[string, string, boolean]> = [
    ['Packages', f.packages != null ? f.packages.toLocaleString('en-AU') : '—', false],
    ['Room nights', f.roomNights != null ? f.roomNights.toLocaleString('en-AU') : '—', false],
    ['Avg LOS', f.avgLos != null ? f.avgLos.toFixed(2) : '—', false],
    ['Blended ADR (AUD)', money(f.blendedAdrAud), false],
    ['TTV (AUD)', money(f.ttvAud), false],
    ['Vendor revenue (AUD)', money(f.vendorRevenueAud), false],
    ['Bundled experience cost (AUD)', money(f.bundledExperienceCostAud), false],
    ['Override comms (AUD)', overrideCommsValue, false],
    ['Gross margin (AUD)', money(f.grossMarginAud), true],
    ['% Margin', marginPctValue, true],
  ];
  return `<div class="card" data-pdf-section><h2>Forecast Summary</h2>${placeholderNote(
    data.isPlaceholderData.forecastSummary,
    'Waiting on the contract FX rate to load',
  )}
    <table><thead><tr><th>Metric</th><th style="text-align:right;color:#0288D1;">${escapeHtml(data.optionName)}</th></tr></thead>
    <tbody>${rows
      .map(
        ([label, value, bold]) =>
          `<tr><td style="color:#5F6168;font-size:12px;${bold ? 'font-weight:500;' : ''}">${label}</td><td style="text-align:right;font-family:'SF Mono',Monaco,Consolas,monospace;font-size:13px;${bold ? 'font-weight:500;' : ''}">${value}</td></tr>`,
      )
      .join('')}</tbody></table></div>`;
}

function previousCampaignSection(data: CommercialApprovalData): string {
  const p = data.previousCampaign;
  const rows: Array<[string, string]> = [
    ['TTV AUD', money(p.ttvAud)],
    ['Margin AUD', money(p.marginAud)],
    ['Margin %', pct(p.marginPct)],
    ['Room Nights', p.roomNights != null ? p.roomNights.toLocaleString('en-AU') : '—'],
    ['Packages', p.packages != null ? p.packages.toLocaleString('en-AU') : '—'],
    ['ADR', money(p.adr)],
    ['Avg Lead Time', p.avgLeadTime != null ? `${p.avgLeadTime} days` : '—'],
    ['Avg LOS', p.avgLos != null ? p.avgLos.toFixed(1) : '—'],
    ['Countries Not To Run', escapeHtml(p.countriesNotToRun) || '—'],
    ['Brand Not Running', escapeHtml(p.brandNotRunning) || '—'],
  ];
  return `<div class="card" data-pdf-section><h2>Previous Campaign Performance</h2>${placeholderNote(
    data.isPlaceholderData.previousCampaign,
    'Margin, room nights, packages, ADR, lead time, and LOS have no data source yet (XTRNT-1002 comment thread)',
  )}
    <table class="prev-camp-table"><tbody>${rows
      .map(([label, value]) => `<tr><td>${label}</td><td>${value}</td></tr>`)
      .join('')}</tbody></table></div>`;
}

/**
 * Builds the standalone Commercial Approval document as a self-contained HTML string.
 * Used verbatim for the downloadable HTML export, and screenshotted section-by-section
 * for the PDF export, so the two outputs are always byte-for-byte the same document.
 */
export function buildCommercialApprovalHtml(data: CommercialApprovalData): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Commercial Approval — ${escapeHtml(data.propertyName)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
:root{--le-midnight:#363A45;--le-white:#FFFFFF;--le-aquamarina:#00F1BD;--grey-50:#FAFAFA;--grey-100:#F4F5F7;--grey-200:#E8EAEE;--grey-600:#5F6168;--grey-700:#45474E;--divider:#E8EAEE;--font-ui:'Roboto',-apple-system,'Helvetica Neue',Arial,sans-serif;}
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:var(--font-ui);font-size:13px;line-height:1.5;color:var(--le-midnight);background:#F7F8FA;}
.page{max-width:1000px;margin:0 auto;padding:0 2rem 3rem;}
.approval-bar{background:var(--le-midnight);color:var(--le-white);padding:14px 0;margin-bottom:1.75rem;}
.approval-bar-inner{max-width:1000px;margin:0 auto;padding:0 2rem;}
.approval-eyebrow{font-size:10.5px;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;color:var(--le-aquamarina);margin-bottom:2px;}
.approval-title{font-size:17px;font-weight:600;}
.approval-meta{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;}
.approval-meta span{font-size:11.5px;font-weight:500;color:rgba(255,255,255,0.85);background:rgba(255,255,255,0.09);border-radius:999px;padding:3px 10px;}
.card{background:#fff;border:1px solid var(--divider);border-radius:10px;padding:20px;margin-bottom:1.25rem;}
.row{display:flex;gap:1.25rem;margin-bottom:1.25rem;align-items:stretch;}
.row .card{flex:1;min-width:0;margin-bottom:0;}
.lead-sell-value{font-size:24px;font-weight:600;}
.note{font-size:12px;color:var(--grey-600);margin-top:6px;}
h2{font-size:11.5px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;color:var(--grey-700);margin-bottom:0.9rem;padding-bottom:8px;border-bottom:1px solid var(--divider);}
table{width:100%;border-collapse:collapse;}
thead th{background:var(--grey-50);padding:9px 10px;text-align:left;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--grey-700);border-bottom:1px solid var(--divider);}
tbody td{padding:8px 10px;border-bottom:1px solid var(--grey-100);font-size:12.5px;}
tbody tr:last-child td{border-bottom:none;}
.deal-info-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;}
.deal-info-field{display:flex;flex-direction:column;gap:4px;background:var(--grey-50);border:1px solid var(--divider);border-radius:6px;padding:12px 14px;}
.deal-info-label{font-size:10.5px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;color:var(--grey-600);}
.deal-info-value{font-size:17px;font-weight:600;}
.prev-camp-table td:first-child{width:42%;font-weight:500;background:var(--grey-50);color:var(--grey-700);}
.placeholder-note{font-size:11px;font-weight:600;color:#ED6C02;background:#FEF3E6;border-radius:6px;padding:6px 10px;margin-bottom:12px;display:inline-block;}
.discount-box{width:60px;text-align:center;font-weight:600;font-size:13px;padding:6px 8px;border:1px solid #00C9A0;border-radius:6px;background:#E6FFF9;color:var(--le-midnight);display:inline-block;}
@media print{@page{margin:1.2cm;}}
</style>
</head>
<body>
<div class="approval-bar" data-pdf-section><div class="approval-bar-inner">
  <div class="approval-eyebrow">Commercial Approval</div>
  <div class="approval-title">${escapeHtml(data.propertyName)}${data.offerCountry ? ` · ${escapeHtml(data.offerCountry)}` : ''}</div>
  <div class="approval-meta">
    <span>${escapeHtml(data.optionName)}</span>
    ${data.lengthsOfStay.length > 0 ? `<span>${Math.min(...data.lengthsOfStay)}-night package</span>` : ''}
    <span>${escapeHtml(data.exportDate)}</span>
  </div>
</div></div>
<div class="page">
<div class="row" data-pdf-section>
${leadSellPriceSection(data)}
${inclusionsSection(data)}
</div>
${dealInformationSection(data)}
${prvChartSection(data)}
${prvByMonthSection(data)}
${blackoutsSection(data)}
${overridesSection(data)}
${forecastSummarySection(data)}
${previousCampaignSection(data)}
</div>
</body>
</html>`;
}
